<template>
  <div class="trend-line-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-data-line"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'trend-line-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { dates: [], times: [], rates: [] }

      const sortedData = [...this.data].sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
      )

      return {
        dates: sortedData.map(item => this.formatDate(item.date)),
        times: sortedData.map(item => item.times || 0),
        rates: sortedData.map(item => item.successRate || 0)
      }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      if (!this.chart || !this.hasData) return

      const { dates, times, rates } = this.chartData

      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            let result = `<div><strong>${params[0].axisValue}</strong><br/>`
            params.forEach(param => {
              if (param.seriesName === '调用次数') {
                result += `${param.marker}${param.seriesName}: <span style="color: #409EFF">${this.formatNumber(param.value)}</span><br/>`
              } else {
                result += `${param.marker}${param.seriesName}: <span style="color: #67C23A">${param.value}%</span><br/>`
              }
            })
            result += '</div>'
            return result
          }
        },
        legend: {
          data: ['调用次数', '成功率'],
          top: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: {
            rotate: dates.length > 15 ? 45 : 0,
            interval: dates.length > 30 ? Math.floor(dates.length / 15) : 'auto'
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '调用次数',
            position: 'left',
            axisLabel: {
              formatter: (value) => {
                if (value >= 10000) return (value / 10000).toFixed(1) + '万'
                if (value >= 1000) return (value / 1000).toFixed(1) + 'k'
                return value
              }
            }
          },
          {
            type: 'value',
            name: '成功率(%)',
            position: 'right',
            min: 0,
            max: 100,
            axisLabel: {
              formatter: '{value}%'
            }
          }
        ],
        series: [
          {
            name: '调用次数',
            type: 'line',
            yAxisIndex: 0,
            data: times,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#667eea'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
                { offset: 1, color: 'rgba(102, 126, 234, 0.05)' }
              ])
            }
          },
          {
            name: '成功率',
            type: 'line',
            yAxisIndex: 1,
            data: rates,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#5daf7f'
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      // 如果是跨年数据或数据量少，显示完整日期
      if (this.data.length <= 31) {
        return dateStr.substring(5) // 显示 MM-DD
      }
      return dateStr.substring(5) // 显示 MM-DD
    },

    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.trend-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 380px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 380px;
  color: #909399;
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/invoice-stats/components/trend-line-chart.vue